/**
 * SequencingHomePage — Phase 1A landing for the new Sequencing System.
 *
 * 4 tiles:
 *   • Groups               → /sequencing/groups
 *   • Type Catalog (admin) → /sequencing/types
 *   • Disclaimers  (admin) → /sequencing/disclaimers
 *   • Holidays             → /sequencing/holidays
 *
 * Header surfaces the Control Plane mode (mock | live) + signed-in
 * persona so an admin can tell at a glance what env they're hitting.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Stack, BookOpenText, ShieldCheck, CalendarBlank, ArrowRight,
  Plugs, GearSix, Lightning, ArrowLeft, Plus, Cpu, FileText,
} from "@phosphor-icons/react";
import { seqStatus } from "@/lib/sequencingApi";
import CpConfigModal from "@/components/sequencing/CpConfigModal";
import { useT } from "@/lib/i18n";

export default function SequencingHomePage({ user }) {
  const t = useT();
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  function refreshStatus() {
    seqStatus()
      .then(setStatus)
      .catch(e => setErr(e?.response?.data?.detail || e?.message || String(e)));
  }
  useEffect(refreshStatus, []);

  const cp = status?.control_plane;
  const isAdmin       = status?.is_admin;
  const isSuperAdmin  = status?.is_super_admin;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sequencing-home">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/" data-testid="sequencing-home-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <Lightning size={22} weight="fill" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            {t("seq.title", "Sequencing System")}
          </h1>
          <p className="text-[11px] text-slate-500 truncate">
            {t("seq.subtitle", "Multi-compressor coordination, commissioning, and live control.")}
          </p>
        </div>
        {cp && (isSuperAdmin ? (
          <button
            data-testid={`sequencing-cp-mode-${cp.mode}`}
            onClick={() => setModalOpen(true)}
            title={cp.base_url || "Click to configure the Control Plane connection."}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border transition-colors
              ${cp.mode === "live"
                ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                : "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"}
              focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer`}>
            <Plugs size={10} weight="fill" className="inline -mt-0.5 mr-1" />
            CP: {cp.mode}
            <GearSix size={10} weight="fill" className="inline -mt-0.5 ml-1 opacity-60" />
          </button>
        ) : (
          <span
            data-testid={`sequencing-cp-mode-${cp.mode}`}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border
              ${cp.mode === "live"
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : "bg-amber-50 text-amber-700 border-amber-300"}`}
            title={cp.base_url || "Mock control plane (no env vars set)"}
          >
            <Plugs size={10} weight="fill" className="inline -mt-0.5 mr-1" />
            CP: {cp.mode}
          </span>
        ))}
      </header>

      <CpConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={refreshStatus} />

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm"
               data-testid="sequencing-home-error">
            {err}
          </div>
        )}

        {/* iter150AH — Group creation is now open to dealers + end-users.
            Tenant scope is enforced server-side; non-admins only build
            groups for compressors inside their own hierarchy. */}
        <Link to="/sequencing/groups/new"
              data-testid="seq-build-group-cta"
              className="block bg-gradient-to-r from-blue-700 to-blue-600 text-white rounded-sm px-4 py-3 hover:from-blue-800 hover:to-blue-700 transition-colors group">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm bg-white/15 inline-flex items-center justify-center shrink-0">
              <Plus size={16} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold font-['Chivo']">Build a new compressor group</div>
              <div className="text-[11px] text-blue-100">
                Pick compressors from your fleet — we create the tenant, site and devices in the Control Plane on the fly.
              </div>
            </div>
            <ArrowRight size={14} weight="bold"
                        className="opacity-70 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </Link>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Tile to="/sequencing/groups"
                icon={<Stack size={20} weight="fill" />}
                title={t("seq.groups_title", "Compressor groups")}
                desc={t("seq.groups_desc", "Create, edit, and assign compressors to coordinated groups.")}
                testId="seq-tile-groups" />

          <Tile to="/sequencing/types"
                icon={<BookOpenText size={20} weight="fill" />}
                title={t("seq.types_title", "Type catalog")}
                desc={t("seq.types_desc", "Maintain the model-level FAD@Pset reference table.")}
                testId="seq-tile-types"
                adminOnly={!isAdmin} />

          <Tile to="/sequencing/disclaimers"
                icon={<ShieldCheck size={20} weight="fill" />}
                title={t("seq.disclaimers_title", "Disclaimers")}
                desc={t("seq.disclaimers_desc", "Operator-acknowledgement text shown during commissioning.")}
                testId="seq-tile-disclaimers"
                adminOnly={!isAdmin} />

          <Tile to="/sequencing/holidays"
                icon={<CalendarBlank size={20} weight="fill" />}
                title={t("seq.holidays_title", "Holiday calendar")}
                desc={t("seq.holidays_desc", "Mark non-running days per tenant. Template seeded for CA + US.")}
                testId="seq-tile-holidays" />

          <Tile to="/sequencing/cheatsheet"
                icon={<FileText size={20} weight="fill" />}
                title={t("seq.cheatsheet_title", "Operator cheat-sheet")}
                desc={t("seq.cheatsheet_desc", "Single-page printable reference: FSM, engine rules, photo capture, emergency actions.")}
                testId="seq-tile-cheatsheet" />

          {isAdmin && (
            <Tile to="/sequencing/admin/device-templates"
                  icon={<Cpu size={20} weight="fill" />}
                  title={t("seq.device_templates_title", "Device template mapping")}
                  desc={t("seq.device_templates_desc", "Map (PLC × inverter × model) to a CP device template. Hides raw template IDs from operators.")}
                  testId="seq-tile-device-templates"
                  adminOnly />
          )}

        </div>

        <div className="rounded-sm border border-slate-200 bg-white p-4 text-xs text-slate-600 space-y-1">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <GearSix size={12} weight="bold" /> {t("seq.status_label", "Module status")}
          </div>
          <div>{t("seq.status_phase", "Phase")}: <span className="font-mono">{status?.phase || "—"}</span></div>
          <div>{t("seq.status_user", "Signed in as")}: <span className="font-mono">{user?.email || status?.user}</span></div>
          <div>{t("seq.status_admin", "Admin")}: <span className="font-mono">{String(!!status?.is_admin)}</span></div>
          <div>{t("seq.status_cp", "Control plane")}: <span className="font-mono">{cp?.mode || "—"}</span>{cp?.base_url ? <span className="ml-1 text-slate-400">({cp.base_url})</span> : null}</div>
        </div>
      </main>
    </div>
  );
}

function Tile({ to, icon, title, desc, testId, adminOnly }) {
  return (
    <Link to={to}
          data-testid={testId}
          className={`bg-white border rounded-sm p-4 hover:border-blue-400 hover:shadow-sm transition group relative
            ${adminOnly ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700 group-hover:bg-blue-100">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold font-['Chivo'] text-slate-900 group-hover:text-blue-700">
            {title}
            {adminOnly && (
              <span className="ml-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 px-1 rounded-sm align-middle">
                admin
              </span>
            )}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
        </div>
        <ArrowRight size={14} weight="bold" className="text-slate-400 group-hover:text-blue-600 shrink-0" />
      </div>
    </Link>
  );
}
